import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Webinar, WebinarClient, WebinarRoom } from '../Services/WOWApi.Service';
import { Subscription } from 'rxjs';
import { CommonService } from '../Services/common.service';
import { LoginServiceService } from '../Services/login-service.service';

interface HomePrizeCard {
  webinarId: number;
  name: string;
  imageUrl: string;
  tag: string;
}

@Component({
    selector: 'app-webinar-rooms',
    templateUrl: './webinar-rooms.component.html',
    styleUrls: ['./webinar-rooms.component.css'],
    standalone: false
})
export class WebinarRoomsComponent implements OnInit, OnDestroy {
  showSpinner: boolean = false;
  webinarRooms: Array<WebinarRoom> = [];
  topWebinars: Array<Webinar>;
  isMobileView = false;
  subscription: Subscription;
  minHeightAllWebinarRooms: number = 0;
  webinarDivDisplayAllRooms = 'none';

  prizeCards: HomePrizeCard[] = [];
  activeIndex = 0;
  private carouselTimer: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;
  private readonly fallbackImage = 'assets/Images/demo1.png';
  private readonly dwellMs = 2000;
  private coversLoaded = false;
  private stepX = 92;
  private visibleSides = 5;

  constructor(private webinarClient: WebinarClient,
    private router: Router,
    private commonService: CommonService,
    private cdr: ChangeDetectorRef,
    private loginServiceService: LoginServiceService
    ) {
      // Removed global spinner initialization for better UX
      // this.commonService.setSpinner(true);
    }

  ngOnInit(): void {
    this.updateCardSpread();
    this.subscription = this.commonService.isMobileView.subscribe(res => this.isMobileView = res);
    this.subscription = this.commonService.isPageReload.subscribe(res => {
      if (res === true) {
        this.commonService.setIsPageReload(false);
        location.reload();
      }
    })
    
    // Make API calls asynchronous and non-blocking
    this.loadDataAsync();
  }

  private async loadDataAsync(): Promise<void> {
    // Start both API calls simultaneously without waiting for each other
    const webinarsPromise = this.GetWebinarsAsync();
    const topWebinarsPromise = this.GetTopWebinarsAsync();
    this.loadPrizeCards();
    
    // Don't wait for completion - let them run in background
    // This allows the UI to render immediately while data loads
    Promise.all([webinarsPromise, topWebinarsPromise]).catch(error => {
      console.error('Error in background API calls:', error);
    });
  }
  // Original synchronous method (kept for backward compatibility)
  GetWebinars() {
    this.showSpinner = true;
    this.webinarClient.getWebinarRooms(true).subscribe(data => {
      this.applyListedRooms(data);
      this.showSpinner = false;
    }, error => { this.showSpinner = false; });
  }

  private async GetWebinarsAsync(): Promise<void> {
    try {
      const data = await this.webinarClient.getWebinarRooms(true).toPromise();
      this.applyListedRooms(data);
    } catch (error) {
      console.error('Error loading webinar rooms:', error);
    }
  }

  get showHostInvite(): boolean {
    try {
      const raw = localStorage.getItem('UserInfo');
      if (!raw) {
        return true;
      }
      const user = JSON.parse(raw);
      return !(user?.isHost || user?.isOwner);
    } catch {
      return true;
    }
  }

  onBecomeHost(): void {
    this.loginServiceService.redirectUrl = '/SignUpHost';
    if (localStorage.getItem('UserInfo')) {
      this.router.navigate(['/SignUpHost']);
      return;
    }
    this.router.navigate(['/Register'], { queryParams: { asHost: '1' } });
  }

  private applyListedRooms(data?: WebinarRoom[] | null): void {
    this.webinarRooms = (data ?? []).filter(room => this.isListedRoom(room));
    this.webinarDivDisplayAllRooms = 'flex';
  }

  private isListedRoom(room: WebinarRoom): boolean {
    if (!room?.roomId) {
      return false;
    }
    const name = (room.roomName || '').trim().toLowerCase();
    return !name.startsWith('virtual tenant');
  }

  roomDescription(desc: string) {
    if (!desc) {
      return '';
    }
    return desc.length > 80 ? desc.slice(0, 80).trim() + '…' : desc;
  }
  
  // Original synchronous method (kept for backward compatibility)
  GetTopWebinars() {
    this.webinarClient.getTopWebinars().subscribe(data => {
      this.topWebinars = data || [];
      this.tryApplyFallbackPrizes(this.topWebinars);
    });
  }

  private async GetTopWebinarsAsync(): Promise<void> {
    try {
      const data = await this.webinarClient.getTopWebinars().toPromise();
      this.topWebinars = data || [];
      this.tryApplyFallbackPrizes(this.topWebinars);
    } catch (error) {
      console.error('Error loading top webinars:', error);
    }
  }

  OpenWebinar(room: any) {
    console.log(room);
    if(room.roomId == null)
    {
      this.router.navigate(["/SignUpHost"]);
    }
    else{
    this.router.navigate(["/Webinars", room.roomId]);
    }
  }

  OpenWebinarSingle(webinarId: number) {
    this.router.navigate(["/Webinar", webinarId]);
  }

  getWebImage(base64String: string) {
    return 'data:image/jpg;base64,' + base64String;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearCarousel();
    this.subscription?.unsubscribe();
  }

  trackPrizeCard(_index: number, card: HomePrizeCard): number {
    return card.webinarId;
  }

  cardOffset(index: number): number {
    const count = this.prizeCards.length;
    if (!count) {
      return 0;
    }
    let diff = ((index - this.activeIndex) % count + count) % count;
    if (diff > count / 2) {
      diff -= count;
    }
    return diff;
  }

  isFar(index: number): boolean {
    return Math.abs(this.cardOffset(index)) > this.visibleSides;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateCardSpread();
  }

  cardStyle(index: number): Record<string, string> {
    const offset = this.cardOffset(index);
    const abs = Math.abs(offset);
    const x = offset * this.stepX;
    const t = this.visibleSides > 0 ? Math.min(1, abs / this.visibleSides) : 0;
    const y = abs * 5;
    const rotateY = offset * -11;
    const scale = 1 - t * 0.48;
    const opacity = this.isFar(index) ? 0 : 1 - t * 0.55;
    const blur = t * 1.05;
    return {
      transform: `translate(-50%, -50%) translateX(${x}px) translateY(${y}px) rotateY(${rotateY}deg) scale(${scale})`,
      opacity: String(opacity),
      zIndex: String(20 - abs),
      filter: `blur(${blur}px)`
    };
  }

  private updateCardSpread(): void {
    const count = this.prizeCards.length;
    this.visibleSides = count < 3 ? 1 : Math.min(5, Math.max(2, Math.floor(count / 2) - 1));
    const width = typeof window === 'undefined' ? 1200 : window.innerWidth;
    const cardW = width <= 576 ? 124 : width <= 900 ? 160 : width <= 1200 ? 188 : width <= 1500 ? 220 : 248;
    const navReserve = width <= 900 ? 20 : 56;
    const outer = Math.max(140, width / 2 - navReserve - cardW * 0.22);
    this.stepX = this.visibleSides > 0 ? outer / this.visibleSides : outer;
  }

  stepCarousel(direction: number): void {
    const count = this.prizeCards.length;
    if (count < 2) {
      return;
    }
    this.activeIndex = (this.activeIndex + direction + count) % count;
    this.startCarousel();
  }

  goToCard(index: number): void {
    this.activeIndex = index;
    this.startCarousel();
  }

  onPrizeImageError(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (image && image.src.indexOf(this.fallbackImage) === -1) {
      image.src = this.fallbackImage;
    }
  }

  private loadPrizeCards(): void {
    this.webinarClient.getRecentWebinarCovers().subscribe({
      next: (webinars) => {
        if (this.destroyed) {
          return;
        }
        this.coversLoaded = true;
        this.applyPrizeCards(webinars);
        this.tryApplyFallbackPrizes(this.topWebinars);
      },
      error: () => {
        this.coversLoaded = true;
        this.tryApplyFallbackPrizes(this.topWebinars);
      }
    });
  }

  private tryApplyFallbackPrizes(webinars?: Webinar[]): void {
    if (this.prizeCards.length || !webinars?.length) {
      return;
    }
    if (!this.coversLoaded) {
      return;
    }
    this.applyPrizeCards(webinars);
  }

  private applyPrizeCards(webinars: Webinar[]): void {
    const cards = (webinars || [])
      .filter(item => !!item?.imageUrl && this.isOpenPrize(item))
      .slice(0, 12)
      .map(item => this.toPrizeCard(item));
    if (!cards.length) {
      return;
    }
    this.prizeCards = cards;
    this.activeIndex = 0;
    this.updateCardSpread();
    this.startCarousel();
  }

  private isOpenPrize(webinar: Webinar): boolean {
    if (webinar?.seatsLeft == null) {
      return true;
    }
    return Number(webinar.seatsLeft) > 0;
  }

  private toPrizeCard(webinar: Webinar): HomePrizeCard {
    return {
      webinarId: webinar.webinarId,
      name: webinar.name || 'LootLucky prize',
      imageUrl: webinar.imageUrl,
      tag: this.tagFor(webinar.startDateTime)
    };
  }

  private tagFor(start?: Date): string {
    if (!start) {
      return 'Live';
    }
    const now = new Date();
    if (start.getTime() <= now.getTime()) {
      return 'Live';
    }
    const sameDay = start.getFullYear() === now.getFullYear()
      && start.getMonth() === now.getMonth()
      && start.getDate() === now.getDate();
    return sameDay ? 'Tonight' : start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  private startCarousel(): void {
    this.clearCarousel();
    if (this.destroyed || this.prizeCards.length < 2) {
      return;
    }
    this.carouselTimer = setInterval(() => this.advanceCarousel(), this.dwellMs);
  }

  private advanceCarousel(): void {
    if (this.destroyed || this.prizeCards.length < 2) {
      return;
    }
    this.activeIndex = (this.activeIndex + 1) % this.prizeCards.length;
    this.cdr.detectChanges();
  }

  private clearCarousel(): void {
    if (this.carouselTimer) {
      clearInterval(this.carouselTimer);
      this.carouselTimer = null;
    }
  }

}
